import { createGetRequest } from 'app/selectors';
import { RequestStatus } from 'features/request/requestSlice';
import { extractValuesByKeys, getCheckedIdsFromObjValues } from 'utils';

import * as R from 'ramda';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { usePreviousValue } from './usePrevious';

type TData<T> = T extends (infer U)[] ? U : T;

/*
 *   TODO: возможно в будущем сделать возможность добавлять не один compareDataRequestId идентификатор
 *    запроса, а массив  и отслеживать сразу несколько запросов
 */

type TCompareData<Data> =
    | {
          compareData: Data;
          compareDataRequestId?: never;
      }
    | {
          compareData?: never;
          compareDataRequestId: string;
      };

type THook<T, K, V, U> = {
    initialData: T;
    uniqueIdentifier: K;
    compareKeys: U[];
    action?: () => void;
    withEffect?: boolean;
} & TCompareData<V>;

const sortByNameCaseInsensitive = (prop: string) => R.sortBy(R.compose(R.toLower, R.toString, R.prop(prop)));
const compareRunner = (first: any, second: any[], action?: () => void) => {
    if (!R.find(R.equals(first), second)) {
        action?.();
    }
};
const equalsRunner = (first: any, second: any, action?: () => void) => {
    if (!R.equals(first, second)) {
        action?.();
    }
};

/*
 * запускает какой-либо action после сравнения пришедших данных (если они не равны)
 * принимает либо данные для сравнения, либо запрос, в котором данные нужно отслеживать с initialData
 * сравнение проходит по пришедшим ключам compareKeys и uniqueIdentifier.
 * uniqueIdentifier - уникальный ключ для идентификации элемента
 * */
export const useRunWithChangeCheck = <
    T,
    K extends keyof NonNullable<TData<T>> & keyof NonNullable<TData<V>> & string,
    V extends Record<string, any>,
    U extends keyof NonNullable<TData<T>> & keyof NonNullable<TData<V>> & string,
>({
    initialData,
    uniqueIdentifier,
    compareData,
    compareKeys,
    action,
    compareDataRequestId,
    withEffect = true,
}: THook<T, K, V, U>) => {
    const prevInitial = usePreviousValue(initialData);
    const isValid = useRef(true);

    const extractKeys = [uniqueIdentifier, ...compareKeys];

    const requestSelector = useMemo(() => createGetRequest(compareDataRequestId ?? ''), [compareDataRequestId]);
    const { data: requestData } = useSelector(requestSelector) ?? {};

    if (!withEffect) {
        return;
    }

    const localCompareData = compareDataRequestId ? extractValuesByKeys(extractKeys, requestData) : compareData;

    if (!R.equals(initialData, prevInitial)) {
        isValid.current = true;
    }

    if (initialData && localCompareData && !R.isEmpty(initialData) && !R.isEmpty(localCompareData) && isValid.current) {
        let initial = null;
        let compare = null;

        if (Array.isArray(initialData)) {
            initial = sortByNameCaseInsensitive(uniqueIdentifier)(
                getCheckedIdsFromObjValues(extractKeys)(initialData) as any[],
            );
        } else if (R.is(Object, initial)) {
            initial = extractValuesByKeys(extractKeys, initialData);
        } else {
            return;
        }

        if (Array.isArray(localCompareData)) {
            compare = sortByNameCaseInsensitive(uniqueIdentifier)(
                getCheckedIdsFromObjValues(extractKeys)(localCompareData) as any[],
            );
        } else if (R.is(Object, localCompareData)) {
            compare = extractValuesByKeys(extractKeys, localCompareData);
        } else {
            return;
        }

        if (Array.isArray(initial) && !Array.isArray(compare)) {
            compareRunner(compare, initial, action);
            isValid.current = false;
        } else if (Array.isArray(compare) && !Array.isArray(initial)) {
            compareRunner(initial, compare, action);
            isValid.current = false;
        } else {
            equalsRunner(initial, compare, action);
            isValid.current = false;
        }
    }
};

/*
 * запускает какой-либо action после успешного завершения запроса
 * */
export const useRunAfterSuccessRequest = ({
    action,
    compareDataRequestId,
    withEffect = true,
    additionalCompareKeys,
}: Pick<THook<any, any, never, any>, 'withEffect' | 'action' | 'compareDataRequestId'> & {
    additionalCompareKeys?: string[];
}) => {
    const isValid = useRef(true);
    const requestSelector = useMemo(() => createGetRequest(compareDataRequestId ?? ''), [compareDataRequestId]);
    const { data: requestData, status: requestStatus } = useSelector(requestSelector) ?? {};

    const baseKeys = ['result', 'message'];
    const extractKeys = additionalCompareKeys ? [...baseKeys, ...additionalCompareKeys] : baseKeys;

    const extracted = extractValuesByKeys(extractKeys, requestData) ?? {};

    const prevExtracted = usePreviousValue(extracted);

    if (!withEffect) {
        return;
    }

    if (!R.equals(prevExtracted, extracted)) {
        isValid.current = true;
    }

    const { message: extractedMsg, result: extractedResult, validation: extractedValidation } = extracted ?? {};
    const isStatusSuccess = requestStatus === RequestStatus.SUCCESS;

    if (isValid.current && !extractedMsg && extractedResult && !extractedValidation && isStatusSuccess) {
        action?.();
        isValid.current = false;
    }
};
